import React, { useState, useEffect } from 'react';
import { Table, InputNumber, Tag, Modal, Descriptions, Button } from 'antd';
import { CrownOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/ro';  // Importing locale for dayjs if needed  
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import './gradingTable.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const GradingTable = ({
    filteredApplications,
    gradingFinished,
    grades,
    handleGradeChange,
    calculateFinalGrade,
    calculateAverageFinalGrade,
    settings,
    mapSizeToType,
    medalWinners,
    userRole,
    openApplicationId // optional prop: when set, open that application in modal
}) => {
    const [modalVisible, setModalVisible] = useState(false);  
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [judgeModalVisible, setJudgeModalVisible] = useState(false);
    const [judges, setJudges] = useState([]);
    const [selectedJudges, setSelectedJudges] = useState([]);

    const JUDGING_CRITERIA = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];  

    // Add this helper to find current application's index  
    const currentIndex = selectedApplication  
    ? filteredApplications.findIndex(app => app._id === selectedApplication._id)  
    : -1;  

    const goToPrevious = () => {  
    if (currentIndex > 0) {  
    setSelectedApplication(filteredApplications[currentIndex - 1]);  
    }  
    };  
    const goToNext = () => {  
    if (currentIndex < filteredApplications.length - 1) {  
    setSelectedApplication(filteredApplications[currentIndex + 1]);  
    }  
    };  

    // Define columns for the table  
    const columns = [
        {
            title: 'Number',
            dataIndex: '',
            key: 'number',
            render: (application) => {
                if (application.group && application.group.group_number) {
                    return application.group.group_number;
                } else if (application.contestant) {
                    return application.contestant.contestant_number;
                } else if (application.contestants && application.contestants.length > 0) {
                    return application.contestants
                        .map((contestant) => `${contestant.first_name} ${contestant.last_name}`)
                        .join(', ');
                } else {
                    return 'No Contestants';
                }
            }
        },
        {
            title: 'Participants',
            dataIndex: '',
            key: 'participants',
            render: (application) => {
                if (application.group && application.group.group_name) {
                    return application.group.group_name;
                } else if (application.contestant) {
                    return `${application.contestant.first_name} ${application.contestant.last_name}`;
                } else if (application.contestants && application.contestants.length > 0) {
                    return application.contestants
                        .map((contestant) => `${contestant.first_name} ${contestant.last_name}`)
                        .join(', ');
                } else {
                    return 'No Contestants';
                }
            },
            sorter: (a, b) => {
                const nameA =
                    (a.group && a.group.group_name) ||
                    (a.contestant && a.contestant.last_name) ||
                    (a.contestants && a.contestants[0] && a.contestants[0].last_name) ||
                    '';
                const nameB =
                    (b.group && b.group.group_name) ||
                    (b.contestant && b.contestant.last_name) ||
                    (b.contestants && b.contestants[0] && b.contestants[0].last_name) ||
                    '';
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: 'Group Type',
            dataIndex: '',
            key: 'groupType',
            render: (application) => {
                if (application.group && application.group.group_size) {
                    return mapSizeToType(application.group.group_size);
                } else {
                    return 'solo';
                }
            },
        },
        {
            title: 'Event',
            dataIndex: 'event',
            key: 'event',
            render: (event) => (event ? `${event.name} - ${event.style}` : 'No Event'),
        },
        {
            title: 'Scheduled Start',
            dataIndex: 'date',
            key: 'date',
            render: (date) => (date ? dayjs.utc(date).tz('Europe/Bucharest').format('YYYY-MM-DD HH:mm') : 'Not Scheduled'),
            sorter: (a, b) => new Date(a.date) - new Date(b.date),
        },
        // Criteria columns  
        ...['entertainment', 'technicality', 'style', 'stage_presence', 'originality'].map((criterion) => ({
            title: criterion.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            dataIndex: '',
            key: criterion,
            className: 'centered',
            render: (application) => {
                // Determine effective role: prefer explicit prop, fall back to global marker
                const effectiveRole = userRole || ((typeof window !== 'undefined' && window.__APP_USER_ROLE__) ? (window.__APP_USER_ROLE__ === 'judge' ? 'judge' : undefined) : undefined);

                if (effectiveRole === 'judge' && !gradingFinished) {
                    return (
                        <InputNumber
                            min={0}
                            max={20}
                            value={grades[application._id]?.[criterion]}
                            onChange={(value) => handleGradeChange(application._id, criterion, value)}
                            onClick={e => e.stopPropagation()}
                        />
                    );
                }

                // Default: show average score across judges
                const applicationGrades = application.grades || [];
                if (applicationGrades.length === 0) return '';
                const total = applicationGrades.reduce((acc, grade) => acc + (grade[criterion] || 0), 0);
                const average = total / applicationGrades.length;
                return average.toFixed(2);
            },
        })),
        {
            title: 'Final Grade',
            dataIndex: '',
            key: 'finalGrade',
            className: 'centered',
            render: (application) => {
                // If there are grades saved on the application (from judges), show the averaged final grade
                const hasSavedGrades = (application.grades && application.grades.length > 0);
                const avgGrade = hasSavedGrades ? calculateAverageFinalGrade(application) : null;

                // If no saved grades, but the current user (judge) has entered local grades, show that sum
                const effectiveRole = userRole || ((typeof window !== 'undefined' && window.__APP_USER_ROLE__) ? (window.__APP_USER_ROLE__ === 'judge' ? 'judge' : undefined) : undefined);
                if (!hasSavedGrades && effectiveRole === 'judge') {
                    const finalGrade = calculateFinalGrade(application);
                    return finalGrade !== null ? finalGrade : '';
                }

                if (avgGrade === null) return '';

                const displayGrade = avgGrade ? avgGrade.toFixed(2) : '';
                let tagColor = '';
                let tagText = '';
                let tagIcon = null;
                const medalWinner = medalWinners.find((winner) => winner._id === application._id);
                if (medalWinner) {
                    const medalClass = medalWinner.medal;
                    if (medalClass === 'Gold') {
                        tagColor = 'darkorange';
                        tagText = 'Gold Medal';
                    } else if (medalClass === 'Silver') {
                        tagColor = 'silver';
                        tagText = 'Silver Medal';
                    } else if (medalClass === 'Bronze') {
                        tagColor = 'brown'; // Changed color for Bronze medal  
                        tagText = 'Bronze Medal';
                    }
                } else {
                    if (avgGrade >= settings.gradingDetails.passingGrade) {
                        tagColor = 'green';
                        tagText = 'Pass';
                        tagIcon = <CheckOutlined style={{ marginLeft: 8 }} />;
                    } else {
                        tagColor = 'red';
                        tagText = 'Fail';
                        tagIcon = <CloseOutlined style={{ marginLeft: 8 }} />;
                    }
                }
                return (
                    <span>
                        {tagText && (
                            <Tag color={tagColor}>
                                {displayGrade}
                                {tagIcon}
                                {medalWinner && <CrownOutlined style={{ marginLeft: 8 }} />}
                            </Tag>
                        )}
                    </span>
                );
            }
        }
    ];

    // When parent asks to open a specific application, open modal
    React.useEffect(() => {
        if (openApplicationId) {
            const app = filteredApplications.find(a => a._id === openApplicationId);
            if (app) {
                setSelectedApplication(app);
                setModalVisible(true);
            }
        }
    }, [openApplicationId, filteredApplications]);

    const fetchJudges = async () => {
        try {
            const { getUsersByRole } = require('../../api/user');
            const j = await getUsersByRole('judge');
            setJudges(j);
        } catch (e) {
            console.error('Failed to fetch judges', e);
        }
    };

    const openJudgeModal = async () => {
        await fetchJudges();
        setSelectedJudges([]);
        setJudgeModalVisible(true);
    };

    const handleSendToJudges = async () => {
        try {
            const { sendGradingSheet } = require('../../api/user');
            await sendGradingSheet(selectedApplication._id, selectedJudges);
            setJudgeModalVisible(false);
            setModalVisible(false);
        } catch (e) {
            console.error('Send failed', e);
        }
    };
    return (
        <div>
        <Table
            dataSource={filteredApplications}
            columns={columns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            rowClassName={(record) => (record.fully_graded || record.fullyGraded) ? 'fully-graded-row' : ''}
            onRow={(record) => ({
                onClick: () => {
                    setSelectedApplication(record);
                    setModalVisible(true);
                },
                style: { cursor: 'pointer' }
            })}
        />
                {!selectedApplication ? null : ( 
                <>
                <Modal  
            title="Application Details"  
            open={modalVisible}  
            onCancel={() => setModalVisible(false)}  
            footer={[  
                <Button  
                  key="prev"  
                  onClick={goToPrevious}  
                  disabled={currentIndex <= 0}  
                >  
                  Previous  
                </Button>,  
                <Button  
                  key="next"  
                  type="primary"  
                  onClick={goToNext}  
                  disabled={currentIndex === -1 || currentIndex >= filteredApplications.length - 1}  
                >  
                  Next  
                </Button>,  
                <Button
                    key="send"
                    onClick={openJudgeModal}
                >
                    Send to Judges
                </Button>,
              ]} 
            width={600}  
            destroyOnClose  
        > 
        <Descriptions bordered column={1}>  
            <Descriptions.Item label="Number">  
            {selectedApplication?.group?.group_number ||  
                selectedApplication?.contestant?.contestant_number ||  
                'N/A'}  
            </Descriptions.Item>  
            <Descriptions.Item label="Participants">  
            {selectedApplication?.group?.group_name ||  
                (selectedApplication?.contestant  
                ? `${selectedApplication.contestant.first_name} ${selectedApplication.contestant.last_name}`  
                : selectedApplication?.contestants?.length  
                    ? selectedApplication.contestants.map(c => `${c.first_name} ${c.last_name}`).join(', ')  
                    : 'No Contestants')}  
            </Descriptions.Item> 
            <Descriptions.Item label="Type">
            {selectedApplication.group ? mapSizeToType(selectedApplication.group.group_size) : "solo"}
            </Descriptions.Item>
            <Descriptions.Item label="Event">
            {selectedApplication.event.name} - {selectedApplication.event.style}
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled Time">
            {selectedApplication.date ? dayjs.utc(selectedApplication.date).tz('Europe/Bucharest').format('YYYY-MM-DD HH:mm') : 'Not Scheduled'}
            </Descriptions.Item>
        </Descriptions>
        </Modal>
        <Modal
            title="Send grading sheet to judges"
            open={judgeModalVisible}
            onCancel={() => setJudgeModalVisible(false)}
            onOk={handleSendToJudges}
        >
            {judges.map(j => (
                <div key={j._id} style={{ marginBottom: 8 }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={selectedJudges.includes(j._id)}
                            onChange={(e) => {
                                if (e.target.checked) setSelectedJudges(prev => [...prev, j._id]);
                                else setSelectedJudges(prev => prev.filter(id => id !== j._id));
                            }}
                        />{' '}
                        {j.first_name} {j.last_name} ({j.email})
                    </label>
                </div>
            ))}
        </Modal>
        </>
    )}
        </div>
    );
};

export default GradingTable;  